import { Bell, Link2, Lock, Shield, Store, type LucideIcon } from 'lucide-react';
import type { User as AuthUser } from '@supabase/supabase-js';
import type { Profile } from '@/types';
import { loadSettingsExtras } from './api/settings';

/* ===========================================================
   Settings domain — the editable surface ported from
   WINR-CURRENT `_v16_settings.js`. Types, tab config,
   option lists, and the draft/handle helpers live here so the
   page, hooks, and tab components share one source of truth.
   =========================================================== */

export type TabId =
  | 'account'
  | 'notifications'
  | 'privacy'
  | 'connections'
  | 'communities';

export type Status = 'online' | 'idle' | 'offline';
export type DmAudience = 'everyone' | 'friends' | 'noone';
export type ProfileVisibility = 'public' | 'members' | 'private';

/** The working copy of every editable field (OG `buildDraft`). */
export interface SettingsDraft {
  // Profile
  avatarUrl: string;
  displayName: string;
  /** Without the leading `@` (stored WITH `@` in the DB). */
  handle: string;
  bio: string;
  status: Status;
  // Account
  email: string;
  // Notifications
  notifPush: boolean;
  notifDigest: boolean;
  notifDM: boolean;
  notifReply: boolean;
  notifMention: boolean;
  notifLive: boolean;
  // Privacy
  privShowOnline: boolean;
  privDMs: DmAudience;
  privProfile: ProfileVisibility;
}

/** Setter that patches one field of the draft (OG `_v16Set`). */
export type SettingsSet = <K extends keyof SettingsDraft>(key: K, value: SettingsDraft[K]) => void;

/** Props every tab body receives — the working draft + its field setter. */
export interface TabProps {
  draft: SettingsDraft;
  set: SettingsSet;
}

export interface TabDef {
  id: TabId;
  label: string;
  icon: LucideIcon;
}

/**
 * Sidebar tabs (Account → … → Communities). The public-profile editing surface
 * (avatar / name / username / bio / status) now lives on the View Profile page
 * (`features/profile/components/ProfileEditor`), so Settings opens on Account.
 */
export const TABS: TabDef[] = [
  { id: 'account', label: 'Account', icon: Lock },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'privacy', label: 'Data & Privacy', icon: Shield },
  { id: 'connections', label: 'Connections', icon: Link2 },
  { id: 'communities', label: 'Communities', icon: Store },
];

export const STATUS_OPTIONS: Status[] = ['online', 'idle', 'offline'];

export const DM_OPTIONS: { value: DmAudience; label: string }[] = [
  { value: 'everyone', label: 'Everyone' },
  { value: 'friends', label: 'Only friends and shared communities' },
  { value: 'noone', label: 'Nobody' },
];

export const PROFILE_VIS_OPTIONS: { value: ProfileVisibility; label: string }[] = [
  { value: 'public', label: 'Public — anyone with the link' },
  { value: 'members', label: 'Members of your communities' },
  { value: 'private', label: 'Private — only friends' },
];

export interface ConnectionDef {
  id: string;
  name: string;
  icon: string;
  meta: string;
}

/** Connected-accounts catalog (OG `renderConnectionsTab` list). */
export const CONNECTIONS: ConnectionDef[] = [
  { id: 'discord', name: 'Discord', icon: '🎮', meta: 'Sync your Discord identity' },
  { id: 'google', name: 'Google', icon: '🔵', meta: 'Email-based sign-in' },
  { id: 'apple', name: 'Apple', icon: '', meta: 'Required on iOS for App Store' },
  { id: 'x', name: 'X / Twitter', icon: '𝕏', meta: 'Cross-post wins to your timeline' },
  { id: 'stripe', name: 'Stripe', icon: '💳', meta: 'Get paid for your community' },
  { id: 'tiktok', name: 'TikTok', icon: '📱', meta: 'Auto-pull views and follower count' },
  { id: 'youtube', name: 'YouTube', icon: '🎬', meta: 'Auto-pull subs and watch time' },
  { id: 'github', name: 'GitHub', icon: '⚙', meta: 'Show your contribution graph' },
];

/* ── Handle helpers (OG `slugifyHandle` / `HANDLE_REGEX` / `handleStatus`) ── */

export const HANDLE_REGEX = /^[a-z0-9_]{3,20}$/;

/** Lowercase, strip illegal chars, collapse spaces → `_`, cap at 20 (OG parity). */
export function slugifyHandle(s: string): string {
  return (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9_\s]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 20);
}

/* ── Draft construction (OG `buildDraft`) ─────────────────── */

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/**
 * Seed the working draft from the `profiles` row + OAuth metadata + the
 * localStorage extras (notification/privacy prefs that have no column in the V2
 * schema yet). Mirrors OG `buildDraft`, which defaulted most toggles to ON.
 */
export function buildDraft(profile: Profile | null, user: AuthUser | null): SettingsDraft {
  const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const extras = loadSettingsExtras();
  return {
    avatarUrl: profile?.avatar_url ?? str(meta.avatar_url) ?? str(meta.picture) ?? '',
    displayName:
      profile?.display_name ?? str(meta.full_name) ?? str(meta.name) ?? '',
    handle: (profile?.handle ?? '').replace(/^@/, ''),
    bio: profile?.bio ?? '',
    status: (profile?.status as Status) || 'online',
    email: profile?.email ?? user?.email ?? '',
    // Notifications — default ON unless explicitly false (OG semantics).
    notifPush: profile?.push_permission === 'granted',
    notifDigest: profile?.email_digest !== false,
    notifDM: extras.notifDM !== false,
    notifReply: extras.notifReply !== false,
    notifMention: extras.notifMention !== false,
    notifLive: extras.notifLive !== false,
    // Privacy
    privShowOnline: extras.privShowOnline !== false,
    privDMs: extras.privDMs ?? 'everyone',
    privProfile: extras.privProfile ?? 'public',
  };
}
